import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { projects, type Project } from "@/data/projects";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { workCopy } from "@/lib/siteCopy";
import { localizeProjects } from "@/lib/localizeProjects";
import type { SiteLanguageCode } from "@/lib/language";

type WorkCardItem = {
  key: string;
  projectId: string;
  title: string;
  category: string;
  image: string;
  accentColor: string;
};

/** Cross column: big-on-top or small-on-top (diagonals match) */
type WorkColumn = {
  key: string;
  bigTop: boolean;
  big: WorkCardItem;
  small: WorkCardItem;
};

/** Shared column frame - sized so ~4 columns fit a typical desktop viewport */
const COL =
  "h-[26rem] w-[17.5rem] sm:h-[30rem] sm:w-[20rem] md:h-[32rem] md:w-[21.5rem] lg:h-[34rem] lg:w-[23rem] xl:h-[36rem] xl:w-[24.5rem]";

const BIG_H =
  "h-[15.5rem] sm:h-[18rem] md:h-[19.25rem] lg:h-[20.5rem] xl:h-[21.75rem]";
const SMALL_H =
  "h-[9.5rem] sm:h-[11rem] md:h-[11.75rem] lg:h-[12.5rem] xl:h-[13.25rem]";

/** Max two images per project */
function collectShots(project: Project): string[] {
  const shots = [project.image];
  const extra = project.gallery?.find(
    (g) => g.type === "image" && g.url !== project.image,
  );
  if (extra) shots.push(extra.url);
  return shots.slice(0, 2);
}

function buildColumns(lang: SiteLanguageCode): WorkColumn[] {
  const cols: WorkColumn[] = [];
  const localized = localizeProjects(projects, lang);

  localized.forEach((project, idx) => {
    const shots = collectShots(project);
    const primary: WorkCardItem = {
      key: `${project.id}-0`,
      projectId: project.id,
      title: project.title,
      category: project.category,
      image: shots[0]!,
      accentColor: project.accentColor,
    };
    const secondary: WorkCardItem = {
      key: `${project.id}-1`,
      projectId: project.id,
      title: project.title,
      category: project.category,
      image: shots[1] ?? shots[0]!,
      accentColor: project.accentColor,
    };

    cols.push({
      key: project.id,
      bigTop: idx % 2 === 0,
      big: primary,
      small: secondary,
    });
  });

  return cols;
}

function WorkCard({
  item,
  onOpen,
  size,
}: {
  item: WorkCardItem;
  onOpen: (id: string) => void;
  size: "big" | "small";
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.projectId)}
      className={cn(
        "group relative w-full shrink-0 overflow-hidden rounded-[1.25rem] sm:rounded-[1.5rem]",
        "border border-black/[0.06] bg-muted shadow-[0_8px_24px_rgba(0,0,0,0.06)]",
        "dark:border-white/[0.08] dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)]",
        "text-left transition-transform duration-300 hover:-translate-y-0.5",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30",
        size === "big" ? BIG_H : SMALL_H,
      )}
      aria-label={`View ${item.title}`}
    >
      <img
        src={item.image}
        alt=""
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        loading="lazy"
        decoding="async"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent"
        aria-hidden
      />
      <div className="absolute inset-x-0 bottom-0 z-10 p-3 sm:p-3.5">
        <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/70">
          {item.category}
        </p>
        <p
          className={cn(
            "mt-1 line-clamp-2 font-semibold leading-snug tracking-tight text-white",
            size === "big" ? "text-[13px] sm:text-sm" : "text-[12px]",
          )}
        >
          {item.title}
        </p>
        <span
          className="mt-2 inline-block h-1 w-8 rounded-full"
          style={{ backgroundColor: item.accentColor }}
        />
      </div>
    </button>
  );
}

function CrossColumn({
  column,
  onOpen,
}: {
  column: WorkColumn;
  onOpen: (id: string) => void;
}) {
  return (
    <div className={cn("flex shrink-0 flex-col justify-between gap-3.5 sm:gap-4", COL)}>
      {column.bigTop ? (
        <>
          <WorkCard item={column.big} onOpen={onOpen} size="big" />
          <WorkCard item={column.small} onOpen={onOpen} size="small" />
        </>
      ) : (
        <>
          <WorkCard item={column.small} onOpen={onOpen} size="small" />
          <WorkCard item={column.big} onOpen={onOpen} size="big" />
        </>
      )}
    </div>
  );
}

export function OurWork() {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = workCopy[lang];
  const reduce = useReducedMotion();
  const columns = useMemo(() => buildColumns(lang), [lang]);
  const loop = useMemo(() => [...columns, ...columns], [columns]);

  const openProject = (id: string) => navigate(`/work/${id}`);

  return (
    <section
      id="our-work"
      className="overflow-hidden bg-background py-16 sm:py-20 lg:py-24"
      aria-labelledby="our-work-heading"
    >
      <div className="mx-auto max-w-3xl px-5 text-center sm:px-6">
        <h2
          id="our-work-heading"
          className="text-balance text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.12]"
        >
          {t.title}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-pretty text-base text-muted-foreground sm:text-lg">
          {t.subtitle}
        </p>
        <Button
          size="lg"
          className="mt-8 rounded-full bg-[#00C853] px-7 text-white hover:bg-[#00B34A]"
          onClick={() => navigate("/work")}
        >
          {t.cta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="relative mt-12 sm:mt-14 lg:mt-16">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent sm:w-20 lg:w-28"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent sm:w-20 lg:w-28"
          aria-hidden
        />

        <div className="work-marquee-track overflow-hidden">
          <div
            className={cn(
              "flex w-max items-stretch gap-4 sm:gap-5 lg:gap-6",
              !reduce && "animate-work-marquee",
            )}
            style={{ animationDuration: "70s" }}
          >
            {loop.map((column, i) => (
              <CrossColumn
                key={`${column.key}-${i}`}
                column={column}
                onOpen={openProject}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
